import { Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Invoice, InvoiceStatus } from '../models/order.model';
import { InvoiceService } from '../services/invoice.service';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Department } from '../models/department.model';
import { DepartmentService } from '../services/department.service';
import { InvoiceComponent } from './invoice/invoice.component';
import { Consultant } from '../models/consultant.model';
import { ConsultantService } from '../services/consultant.service';
import { FormControl } from '@angular/forms';
import { combineLatest, concat, debounceTime, distinctUntilChanged, map, merge, mergeMap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { PagedResult } from '../models/paged-result.model';
import { PaginatorIntl } from '../PaginatorIntl';

@Component({
    selector: 'app-invoices',
    templateUrl: './invoices.component.html',
    styleUrls: ['./invoices.component.scss'],
    providers: [{ provide: MatPaginatorIntl, useClass: PaginatorIntl }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InvoicesComponent {
  loading = true;
  displayedColumns: string[] = ['actions', 'economicInvoiceNumber', 'date', 'taskNumber', 'projectLead', 'department', 'customerName', 'total'];
  @ViewChild(MatTable<Invoice>) table: MatTable<Invoice>;
  dataSource: MatTableDataSource<Invoice> = new MatTableDataSource([]);
  @ViewChild(MatSort) sorter: MatSort;
  departments: Department[];
  consultants: Consultant[];
  invoices: Invoice[];

  departmentControl = new FormControl();
  consultantControl = new FormControl();
  filterControl = new FormControl<string>("");
  showManualControl = new FormControl(true);

  @ViewChild(MatPaginator) paginator: MatPaginator;
  page: PagedResult<Invoice>;

  constructor(
    private snackBar: MatSnackBar,
    protected dialog: MatDialog,
    protected invoiceService: InvoiceService,
    protected departmentsService: DepartmentService,
    public consultantsService: ConsultantService,
    protected auth: AuthService
  ) {

  }

  async ngAfterViewInit(): Promise<void> {
    this.consultants = await this.consultantsService.list();
    this.departments = await this.departmentsService.list();
    this.dataSource = new MatTableDataSource([]);
    // this.dataSource.paginator = this.paginator;
    this.loading = false;
    setTimeout(() => {
      this.consultantControl.setValue(this.consultants.find(_ => _.email == this.auth.activeAccount()?.username)?.id);
    });

    merge(
      this.departmentControl.valueChanges,
      this.consultantControl.valueChanges,
      this.filterControl.valueChanges,
      this.showManualControl.valueChanges
    ).pipe(distinctUntilChanged(), debounceTime(500),
      map(_ => ({ department: this.departmentControl.value, consultant: this.consultantControl.value, taskNumber: this.filterControl.value, showManual: this.showManualControl.value }))
    ).subscribe(_ => this.loadInvoices(this.paginator.pageIndex, this.paginator.pageSize, this.sorter.active, this.sorter.direction));
    this.setupSort();
    await this.loadInvoices(this.paginator.pageIndex, this.paginator.pageSize, this.sorter.active, this.sorter.direction);
  }

  onPage(page: PageEvent) {
    this.loadInvoices(page.pageIndex, page.pageSize, this.sorter.active, this.sorter.direction);
  }

  setupSort() {
    this.sorter.sortChange.subscribe((sort) => {
      this.paginator.pageIndex = 0;
      this.loadInvoices(0, this.paginator.pageSize, sort.active, sort.direction);
    });
  }

  async loadInvoices(pageIndex: number, pageSize: number, sortBy: string, sortDirection: string) {
    this.page = await this.invoiceService.list(
      pageIndex ?? 0, pageSize ?? 50,
      this.consultantControl.value, this.departmentControl.value,
      this.filterControl.value, this.showManualControl.value,
      sortBy, sortDirection
    );
    this.dataSource.data = this.page.items;
  }

  statusName(s: InvoiceStatus) {
    switch (s) {
      case InvoiceStatus.Open:
        return "Åben";
      case InvoiceStatus.SentToEconomic:
        return "Sendt";
      case InvoiceStatus.Manual:
        return "Manuel";
      default: return "Ukendt";
    }
  }

  consultant(id: string) {
    return this.consultants?.find(_ => _.id == id);
  }

  department(id: string) {
    return this.departments.find(_ => _.id == id)?.name;
  }

  openInvoice(invoice: Invoice) {
    let clone: Invoice = { ...invoice };
    clone.salesRealizatorDistribution = clone.salesRealizatorDistribution.map(_ => ({ ..._ }));
    clone.distribution = clone.distribution.map(_ => ({ ..._ }));
    clone.tasks = clone.tasks.map(_ => ({ ..._ }));

    let dialogRef = this.dialog.open(InvoiceComponent, {
      data: { invoice: clone },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(async updated => {
      console.log("Updated", updated);
      if (updated) {
        let idx = this.dataSource.data.findIndex(_ => _.id == updated.id);
        this.dataSource.data[idx] = updated;
        this.table.renderRows();
        this.loadInvoices(this.paginator.pageIndex, this.paginator.pageSize, this.sorter.active, this.sorter.direction);
      }
    });
  }

  duplicateInvoice(invoice: Invoice) {
    let clone: Invoice = { ...invoice };
    clone.id = undefined;
    clone.date = new Date();

    if (clone.status === InvoiceStatus.SentToEconomic) {
      clone.status = InvoiceStatus.Open;
    }

    clone.tasks = (clone.tasks ?? []).map(t => ({ ...t, id: undefined }));
    clone.transport = (clone.transport ?? []).map(t => ({ ...t, id: undefined }));
    clone.expenses = (clone.expenses ?? []).map(e => ({ ...e }));
    clone.distribution = (clone.distribution ?? []).map(d => ({ ...d, id: undefined, invoiceId: undefined }));
    clone.salesRealizatorDistribution = (clone.salesRealizatorDistribution ?? []).map(d => ({ ...d, id: undefined, invoiceId1: undefined }));

    if (clone.leadCreatorDistribution) {
      clone.leadCreatorDistribution = { ...clone.leadCreatorDistribution, id: undefined, invoiceId2: undefined };
    }

    let dialogRef = this.dialog.open(InvoiceComponent, {
      data: { invoice: clone, duplicate: true },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(async updated => {
      if (updated) {
        this.loadInvoices(this.paginator.pageIndex, this.paginator.pageSize, this.sorter.active, this.sorter.direction);
      }
    });
  }

  async removeInvoice(invoice: Invoice) {
    if (window.confirm(`Vil du slette faktura til ${invoice.customerName} på DKK ${this.invoiceService.totalIncludingExpenses(invoice)}`)) {
      await this.invoiceService.delete(invoice.id);
      this.dataSource.data = this.dataSource.data.filter(_ => _.id != invoice.id);
      this.snackBar.open("Faktura slettet");
    }
  }
}
